import { AuthType } from '@thesavingsgroup/auth/AuthType';
import { DeliveryMechanism } from '@thesavingsgroup/auth/DeliveryMechanism';
import i18n from 'i18next';
import lodashGet from 'lodash/get';
import toString from 'lodash/toString';
import { StateCreator } from 'zustand';

import { DeliverPinLocation } from './enums/DeliverPinLocation';
import { extractPinDeliverErrorStatus } from './functions/extractPinDeliverErrorStatus';
import { DeliverPinParams } from './types/DeliverPinParams';
import { PinState } from './types/PinState';
import { PinStateDependencies } from './types/PinStateDependencies';

export const pinStore =
  ({
    useAnalyticStore,
    useApiStore,
  }: PinStateDependencies): StateCreator<PinState> =>
  (set) => ({
    deliverPinFailedMessage: null,
    deliverPinRetrySuccess: false,
    deliverPinSuccess: false,
    primaryPhoneNumber: null,

    deliverPin: (params: DeliverPinParams): Promise<void> => {
      const {
        sendUserActivatingAccountAnalytics,
        sendUserActivatingApplicationAnalytics,
        sendUserAuthenticatingAnalytics,
        sendUserRegisteringAnalytics,
      } = useAnalyticStore.getState();

      set({
        deliverPinFailedMessage: null,
        deliverPinRetrySuccess: false,
        deliverPinSuccess: false,
      });

      const { deliverPinLocation, email, phone, purpose } = params;

      if (purpose === AuthType.ACCOUNT_ACTIVATION_USING_PIN) {
        sendUserActivatingAccountAnalytics();
      }

      if (purpose === AuthType.APPLICATION_ACTIVATION_USING_PIN) {
        sendUserActivatingApplicationAnalytics();
      }

      if (purpose === AuthType.ACCOUNT_REGISTRATION_USING_PIN) {
        sendUserRegisteringAnalytics();
      }

      if (purpose === AuthType.ACCOUNT_SIGN_IN_USING_PIN) {
        sendUserAuthenticatingAnalytics();
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .post('pin/deliver', {
          deliverWith: DeliveryMechanism.SMS,
          email,
          phone,
          purpose,
        })
        .then((): void => {
          set({
            primaryPhoneNumber: phone,
            ...(deliverPinLocation === DeliverPinLocation.SIGN_IN && {
              deliverPinSuccess: true,
            }),
            ...(deliverPinLocation === DeliverPinLocation.RETRY && {
              deliverPinRetrySuccess: true,
            }),
          });
        })
        .catch((error: unknown): void => {
          const status = extractPinDeliverErrorStatus(error);

          set({
            deliverPinFailedMessage: lodashGet(
              i18n.t('errors.deliverPin.statusMessage'),
              toString(status),
              i18n.t('errors.deliverPin.defaultMessage'),
            ),
          });
        });
    },
  });
